import type { ReactElement } from 'react';
import { cloneElement, useContext } from 'react';

import type { SendMethod } from '@rpldy/sender'
import { Uploady, type UploadyProps } from '@rpldy/uploady'

import { type IUploadProps } from './Upload'
import { type IUIProviderValue, UIContext } from '@ui/core/UIContext'

export interface IBaseUploadProps {
  endpoint?: string
  multiple?: boolean
  children?: ReactElement
  /**
   * Determines the allow mimetypes in the file dialog
   * ie: ['image/png', 'image/jpeg', 'application/pdf']
   */
  allowedFileTypes?: string[]
  customSender?: SendMethod
  /**
   * Custom params to send in the payload
   */
  params?: UploadyProps['params']
}

const getProps = (
  props: IBaseUploadProps,
  uiContextProps: IUIProviderValue['Upload'] = {}
): {
  uploadyProps: UploadyProps
  uploadComponentProps: Partial<IUploadProps>
} => {
  const defaultedProps = {
    ...(uiContextProps ?? {}),
    ...props,
  }
  const {
    endpoint = '/',
    multiple = true,
    allowedFileTypes = [],
    customSender,
    params,
    ...uploadProps
  } = defaultedProps

  const uploadyProps = {
    autoUpload: true,
    concurrent: true,
    multiple,
    destination: {
      url: endpoint,
    },
    accept: allowedFileTypes.join(', '),
    send: customSender,
    params,
  }

  const uploadComponentProps: Partial<IUploadProps> = {
    ...uploadProps,
  }

  return {
    uploadyProps,
    uploadComponentProps,
  }
}

export const BaseUpload = (props: IBaseUploadProps) => {
  const { children } = props

  const uiContext = useContext(UIContext)

  const { uploadyProps, uploadComponentProps } = getProps(
    props,
    uiContext?.Upload
  )

  return (
    <Uploady {...uploadyProps} customInput>
      {cloneElement(children, {
        ...uploadComponentProps,
      })}
    </Uploady>
  );
}

BaseUpload.displayName = 'BaseUpload'
